import { useCallback, useEffect, useRef } from 'react';
import { useMessages } from 'dignity.js/react';
import {
  listCertificates,
  saveCertificate
} from '../../p2p/certificateStore.js';
import { LOBBY_SCOPE } from '../../p2p/node.js';
import { verifyDetachedTimeWeaveProof } from '../../p2p/timeWeave.js';

export const LOBBY_CERT_REQUEST = 'dechess-lobby-cert-request';
export const LOBBY_CERT_BATCH = 'dechess-lobby-cert-batch';
const OWNER_BATCH_SIZE = 50;
const ARCHIVIST_BATCH_SIZE = 500;
const REQUEST_INTERVAL_MS = 8_000;
const ARCHIVIST_REQUEST_INTERVAL_MS = 5_000;

function verificationOptions() {
  return {
    verifyVdfProof: async (proof, resultCertificate, player) =>
      (await verifyDetachedTimeWeaveProof(proof, {
        gameId: resultCertificate.gameId,
        contractHash: resultCertificate.contractHash,
        playerId: player.playerId
      })).valid
  };
}

/**
 * Pull recent result certificates from whoever is already in the lobby so a
 * new peer can rebuild rankings without waiting on archivist shard coverage.
 */
export default function LobbyCertificateSync({
  node,
  identity,
  joined = false,
  connectPeerIds = [],
  archivist = false,
  onStored
}) {
  const batchSize = archivist ? ARCHIVIST_BATCH_SIZE : OWNER_BATCH_SIZE;
  const requestIntervalMs = archivist ? ARCHIVIST_REQUEST_INTERVAL_MS : REQUEST_INTERVAL_MS;
  const filter = useCallback(
    (message) =>
      message.type === LOBBY_CERT_REQUEST || message.type === LOBBY_CERT_BATCH,
    []
  );
  const messages = useMessages(node, filter);
  const processed = useRef(0);
  const knownHashesRef = useRef(new Set());

  const broadcast = useCallback(
    (type, payload, peers = []) => {
      if (!node) {
        return Promise.resolve();
      }
      const targets = [...new Set((peers || []).filter(Boolean))];
      return node.broadcastMessage(
        type,
        payload,
        {
          broadcastScope: LOBBY_SCOPE,
          connectToPeers: targets,
          ...(targets.length ? { fanoutPeerIds: targets } : {})
        }
      );
    },
    [node]
  );

  useEffect(() => {
    if (!node || !joined || !identity?.playerId) {
      return undefined;
    }

    let cancelled = false;
    const request = async () => {
      try {
        const local = await listCertificates();
        if (cancelled) {
          return;
        }
        knownHashesRef.current = new Set(
          local.map((certificate) => certificate.certificateHash).filter(Boolean)
        );
        await broadcast(
          LOBBY_CERT_REQUEST,
          {
            requesterId: identity.playerId,
            knownHashes: [...knownHashesRef.current].slice(-200)
          },
          connectPeerIds
        );
      } catch {
        // Lobby peers may still be dialing.
      }
    };

    void request();
    const timer = window.setInterval(() => {
      void request();
    }, requestIntervalMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [broadcast, connectPeerIds, identity?.playerId, joined, node, requestIntervalMs]);

  useEffect(() => {
    if (messages.length < processed.current) {
      processed.current = 0;
    }
    if (processed.current >= messages.length) {
      return;
    }
    const pending = messages.slice(processed.current);
    processed.current = messages.length;

    const process = async () => {
      for (const message of pending) {
        if (message.type === LOBBY_CERT_REQUEST) {
          if (message.payload?.requesterId === identity?.playerId) {
            continue;
          }
          const known = new Set(
            Array.isArray(message.payload?.knownHashes)
              ? message.payload.knownHashes
              : []
          );
          const certificates = (await listCertificates())
            .filter((certificate) =>
              certificate?.certificateHash
              && !known.has(certificate.certificateHash)
            )
            .slice(-batchSize);
          if (!certificates.length) {
            continue;
          }
          await broadcast(
            LOBBY_CERT_BATCH,
            { certificates },
            [message.senderId].filter(Boolean)
          );
          continue;
        }

        if (message.type !== LOBBY_CERT_BATCH) {
          continue;
        }
        const incoming = Array.isArray(message.payload?.certificates)
          ? message.payload.certificates.slice(0, batchSize)
          : [];
        const options = verificationOptions();
        for (const certificate of incoming) {
          if (
            !certificate?.certificateHash
            || knownHashesRef.current.has(certificate.certificateHash)
          ) {
            continue;
          }
          try {
            await saveCertificate(certificate, options);
            knownHashesRef.current.add(certificate.certificateHash);
            onStored?.(certificate);
          } catch {
            // Invalid or incomplete certificates stay out of local rankings.
          }
        }
      }
    };

    void process();
  }, [batchSize, broadcast, identity?.playerId, messages, onStored]);

  return null;
}
